import { createSelector } from "@reduxjs/toolkit";
import React, { createContext, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  getCenterDiamondPrices as onGetCenterDiamondPrices,
  getJewelleryMetals as onGetJewelleryMetals,
} from "slices/thunk";
import { countDiamondPrices as onCountDiamondPrices } from "helpers/backend_helper";
import { convertUsdToInr } from "utils/currencyConverter";

interface Diamond {
  id: number | string;
  carat: number;
  color: string;
  clarity: string;
  shape: string;
  pieces: number;
}

export interface PriceResponse {
  id: number | string;
  price: number;
}

interface Jewellery {
  metal: {
    [key: string]: number; // Assuming the weight is stored as a number
  };
  diamonds: Diamond[]; // Assuming you have a Diamond interface
  shape: string;
  isDiscounted: boolean;
  // Add other properties as needed
}

interface JewelleryContextType {
  calculateMetalPrice: (weight: number, type: string, carat: string) => void;
  handleJewelleryPrice: (
    jewellery: Jewellery,
    selectedMetal: "yellowgold" | "rosegold" | "whitegold",
    selectedKarat: "14k" | "18k",
    // diamondPrice: PriceResponse[],
    caratSize: number,
    priceType: "combined" | "separate"
  ) =>
    | number
    | { metalPrice: number; diamondPrices: number; centerStonePrice: number; sideStonesPrice: number }
    | {
      priceWithDiscount: number; priceWithoutDiscount: number, subTotal: number;
      taxAmount: number;
      discountPrice: number;
      totalPrice: number;
    };
  // handleOrderJewelleryPrice: (
  //   jewellery: Jewellery,
  //   selectedMetal: "yellowgold" | "rosegold" | "whitegold",
  //   selectedKarat: "14k" | "18k",
  //   // diamondPrice: PriceResponse[],
  //   priceType: "combined",
  //   margin: number,
  //   tax: number
  // ) => {
  //   subTotal: number;
  //   taxAmount: number;
  //   discountPrice: number;
  //   totalPrice: number;
  // };
  getDiamondPrices: (diamond: Diamond[]) => void;
}

// Create a context
const JewelleryContext = createContext<JewelleryContextType | undefined>(
  undefined
);

// const centerDiamond = [
//   {
//     _id: "6663eb019ce970b0367bd44b",
//     caratsFrom: 0.93,
//     caratsTo: 1.029,
//     color: "DEF",
//     clarity: "VVS",
//     prices: {
//       round: 370,
//       fancy: 370,
//     },
//   },
//   {
//     _id: "6663eb019ce970b0367bd45b",
//     caratsFrom: 1.49,
//     caratsTo: 1.669,
//     color: "DEF",
//     clarity: "VVS",
//     prices: {
//       round: 380,
//       fancy: 380,
//     },
//   },
//   {
//     _id: "6663eb019ce970b0367bd463",
//     caratsFrom: 1.75,
//     caratsTo: 2.039,
//     color: "DEF",
//     clarity: "VVS",
//     prices: {
//       round: 390,
//       fancy: 390,
//     },
//   },
//   {
//     _id: "6663eb019ce970b0367bd46f",
//     caratsFrom: 2.43,
//     caratsTo: 2.549,
//     color: "DEF",
//     clarity: "VVS",
//     prices: {
//       round: 390,
//       fancy: 390,
//     },
//   },
// ];

// Context Provider Component

export const JewelleryPriceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch<any>();
  const selectDataList = createSelector(
    (state: any) => state.Jewellery,
    (state) => ({
      metals: state.metals,
    })
  );

  const { cetnerDiamondPrices } = useSelector((state: any) => state.Diamond);

  const { metals } = useSelector(selectDataList);

  // Function to calculate price
  const calculateMetalPrice = (
    weight: number,
    type: string,
    carat: string
  ): number | null => {
    // Find the metal data based on type and carat

    const metal = metals.find(
      (metal: any) => metal.type === type && metal.carat === carat
    );

    const totalMetalPrice = weight * metal?.price;

    if (metal) {
      // Calculate total price
      return totalMetalPrice;
    }
    // Return null if no matching metal is found
    return 0;
  };

  const getDiamondPrices = async (diamond: Diamond[]) => {
    try {
      const res: any = await onCountDiamondPrices({
        diamonds: diamond,
      });

      return res?.result;
    } catch (error) {
      console.error("Error fetching diamond prices:", error);
    }
  };

  const calculateDiamondPrices = (
    jewellery: any,
    caratSize: number
  ): { centerStonePrice: number; sideStonesPrice: number; totalPrice: number } => {
    const foundDiamond: any = cetnerDiamondPrices?.find((item: any) => {
      return caratSize >= item.caratsFrom && caratSize <= item.caratsTo;
    });

    const fancyPrice = foundDiamond?.prices?.fancy * caratSize;
    const sideDiamondsPrice = jewellery?.diamonds?.map((diamond: any) => {
      return diamond?.price;
    });

    const totalSideDiamondsPrice = sideDiamondsPrice?.reduce(
      (sum: number, price: number) => sum + price,
      0
    );

    return {
      centerStonePrice: fancyPrice || 0,
      sideStonesPrice: totalSideDiamondsPrice || 0,
      totalPrice: (totalSideDiamondsPrice || 0) + (fancyPrice || 0)
    };
  };

  useEffect(() => {
    dispatch(onGetJewelleryMetals());
    dispatch(onGetCenterDiamondPrices([1, 1.5, 2, 2.5]));
  }, [dispatch]);

  const handleJewelleryPrice = (
    jewellery: any,
    selectedMetal: "yellowgold" | "rosegold" | "whitegold",
    selectedKarat: "14k" | "18k",
    // diamondPrice: PriceResponse[],
    caratSize: number,
    priceType: "combined" | "separate" = "combined"
  ):
    | number
    | { metalPrice: number; diamondPrices: number; centerStonePrice: number; sideStonesPrice: number }
    | {
      priceWithDiscount: number;
      priceWithoutDiscount: number;
      subTotal: number;
      taxAmount: number;
      discountPrice: number;
      totalPrice: number;
    } => {
    // const margin = process.env.NEXT_PUBLIC_PROFIT_MARGIN;
    // const tax = process.env.NEXT_PUBLIC_TAX;
    const metalPrice = Number(
      calculateMetalPrice(
        jewellery?.metal?.[selectedKarat],
        selectedMetal,
        selectedKarat
      )
    );

    const currentDate = new Date();
    const startDate = new Date(jewellery?.discount?.startDate || "");
    const endDate = new Date(jewellery?.discount?.endDate || "");

    const isDiscountActive = currentDate >= startDate && currentDate <= endDate;

    const diamondPricesObj = calculateDiamondPrices(jewellery, caratSize);
    const diamondPrices = diamondPricesObj?.totalPrice || 0;
    // const diamondPrices = convertUsdToInr(usdDiamondPrice)

    const totalJewelleryPrice = metalPrice + diamondPrices;
    // Calculate the final price based on the discount type

    const MarginAmount = calculateMargin(
      totalJewelleryPrice,
      Number(process.env.REACT_APP_JEWELLERY_MARGIN_PRICE)
    );

    const priceWithMargin = totalJewelleryPrice + MarginAmount;
    const taxAmount = calculateTax(
      priceWithMargin,
      Number(process.env.REACT_APP_JEWELLERY_TAX_PRICE)
    );

    const PriceWithTax = priceWithMargin + taxAmount;

    const discountPrice = calculateDiscount(
      jewellery,
      PriceWithTax,
      isDiscountActive
    );

    const totalPrice = PriceWithTax - discountPrice;

    if (priceType === "combined") {
      return {
        priceWithDiscount: Math.floor(totalPrice),
        priceWithoutDiscount: Math.floor(PriceWithTax),
        subTotal: totalJewelleryPrice,
        taxAmount,
        discountPrice,
        totalPrice: totalPrice,
      }; // Return combined price
    } else {
      return { 
        metalPrice, 
        diamondPrices,
        centerStonePrice: diamondPricesObj?.centerStonePrice || 0,
        sideStonesPrice: diamondPricesObj?.sideStonesPrice || 0
      }; // Return separate prices
    }
  };

  const calculateTax = (amount: number, tax: number): number => {
    return (amount * tax) / 100;
  };
  const calculateMargin = (amount: number, margin: number): number => {
    return (amount * margin) / 100;
  };

  const calculateDiscount = (
    jewellery: any,
    priceWithMargin: number,
    isDiscountActive: boolean
  ): number => {
    if (jewellery?.isDiscounted && isDiscountActive) {
      const { type, value } = jewellery.discount;

      if (type === "percentage") {
        const discount = (priceWithMargin * value) / 100;
        return Number(discount.toFixed(2)); // return only discount amount
      } else if (type === "fixed") {
        return Number(value.toFixed(2));
      }
    }

    return 0; // No discount
  };

  return (
    <JewelleryContext.Provider
      value={{
        calculateMetalPrice,
        handleJewelleryPrice,
        getDiamondPrices,
        // handleOrderJewelleryPrice,
      }}
    >
      {children}
    </JewelleryContext.Provider>
  );
};

export const useJewelleryContext = (): JewelleryContextType => {
  const context = useContext(JewelleryContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
